'use client';

import { c as _c } from "react/compiler-runtime";
import { getCookies } from 'cookies-next/client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { maxAge, minAge, vulnerableCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { buildStorageFingerprint, createSessionStorage } from '@sola/shared-utils/session-storage/session-storage.ts';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { SacFiltersEnum } from '../../utils/query-params.sac.tsx';
const defaultLimit = 12;
const maxPages = 9;
/** Cache TTL — 5 minutes. */
const cacheTtlMs = 5 * 60 * 1000;
const scrollToElement = (childId?: string) => {
  if (!childId) {
    return;
  }
  requestAnimationFrame(() => {
    document.querySelector(`.SacCard__bio-link[href*="${childId}"]`)?.scrollIntoView({
      behavior: 'instant',
      block: 'start'
    });
  });
};

/** Narrow countries to HVC overlap when the vulnerable filter is active. */
export const resolveFilteredCountries = (countries: string[] | null, isVulnerable: boolean | undefined): string[] | undefined => {
  if (!isVulnerable) {
    return countries ? [...countries] : undefined;
  }
  const hvc = vulnerableCountryIsoCodes as readonly string[];
  const selected = countries ? [...countries] : undefined;
  if (selected?.length) {
    const matched = selected.filter(c => hvc.includes(c));
    return matched.length > 0 ? matched : selected;
  }
  return [...vulnerableCountryIsoCodes];
};

/** Normalize gender — treat "Either" as unfiltered. */
export const normalizeGender = (gender?: string | null): string | null => gender && gender !== ChildGenderEnum.Either ? gender : null;

/** Map chip filter strings → boolean flags. */
export const resolveChipFilters = (filters?: string[]) => ({
  disability: filters?.includes(SacFiltersEnum.enum.hasDisabilities) || undefined,
  isInHivAffectedArea: filters?.includes(SacFiltersEnum.enum.affectedHiv) || undefined,
  isOrphan: filters?.includes(SacFiltersEnum.enum.orphanedChild) || undefined,
  isVulnerable: filters?.includes(SacFiltersEnum.enum.vulnerableExploitation) || undefined
});
export type ResolvedSacFilters = {
  gender: string | null;
  ageMin: number | null;
  ageMax: number | null;
  countries: string[] | null;
  birthDay: string | null;
  birthMonth: string | null;
  disability: boolean | undefined;
  isInHivAffectedArea: boolean | undefined;
  isOrphan: boolean | undefined;
  isVulnerable: boolean | undefined;
  limit: number;
};

/** True when any filter differs from the default/empty state. */
export const hasCriteria = (f: ResolvedSacFilters): boolean => !!(f.gender && f.gender !== ChildGenderEnum.Either || (f.ageMin && f.ageMax ? f.ageMin !== minAge || f.ageMax !== maxAge : f.ageMin || f.ageMax) || (f.countries?.length ?? 0) > 0 || f.birthDay || f.birthMonth || f.isInHivAffectedArea || f.disability || f.isOrphan || f.isVulnerable);

/** Map CMS props → ResolvedSacFilters (non-live embeds). */
export const resolveFiltersFromProps = (filters: SacFilterProps): ResolvedSacFilters => ({
  gender: normalizeGender(filters.gender),
  ageMin: filters.ageMin ?? null,
  ageMax: filters.ageMax ?? null,
  countries: filters.countries ?? null,
  birthDay: null,
  birthMonth: null,
  disability: filters.disability || undefined,
  isInHivAffectedArea: filters.hiv || undefined,
  isOrphan: filters.orphan || undefined,
  isVulnerable: filters.highlyVulnerable || undefined,
  limit: filters.limit ?? defaultLimit
});

/** Map URL query params → ResolvedSacFilters (live SAC page). */
export const resolveFiltersFromQueryParams = (sacFilters: SacQueryParams, limit = defaultLimit): ResolvedSacFilters => ({
  gender: normalizeGender(sacFilters.gender),
  ageMin: sacFilters.ageMin ?? null,
  ageMax: sacFilters.ageMax ?? null,
  countries: sacFilters.countries?.filter(Boolean) ?? null,
  birthDay: sacFilters.birthDay ? String(sacFilters.birthDay) : null,
  birthMonth: sacFilters.birthMonth ? String(sacFilters.birthMonth) : null,
  ...resolveChipFilters(sacFilters.filters),
  limit
});
export const useSacList = (resolved, isDynamic) => {
  const $ = _c(70);
  const {
    gender,
    ageMin,
    ageMax,
    countries,
    birthDay,
    birthMonth,
    disability,
    isInHivAffectedArea,
    isOrphan,
    isVulnerable,
    limit
  } = resolved;
  let t0;
  if ($[0] !== countries || $[1] !== isVulnerable) {
    t0 = resolveFilteredCountries(countries, isVulnerable);
    $[0] = countries;
    $[1] = isVulnerable;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const filteredCountries = t0;
  let t1;
  if ($[3] !== resolved) {
    t1 = hasCriteria(resolved);
    $[3] = resolved;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const hasActiveCriteria = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = createSessionStorage();
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const storage = t2;
  let t3;
  if ($[6] !== resolved) {
    t3 = buildStorageFingerprint(resolved);
    $[6] = resolved;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const filterFingerprint = t3;
  let t4;
  if ($[8] !== filterFingerprint) {
    t4 = name => `${name}:${filterFingerprint}`;
    $[8] = filterFingerprint;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const key = t4;
  const [page, setPage] = useState(0);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const [storedChildren, setStoredChildren] = useState(t5);
  const [isLoading, setIsLoading] = useState(true);
  const selectedChildRef = useRef(undefined);
  const pathname = usePathname();
  const router = useRouter();
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getCookies();
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const cookieStore = t6;
  const params = useSearchParams();
  let t7;
  if ($[12] !== pathname || $[13] !== router) {
    t7 = () => router.push(pathname);
    $[12] = pathname;
    $[13] = router;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const resetFilters = t7;
  let t8;
  if ($[15] !== limit || $[16] !== page || $[17] !== storedChildren) {
    t8 = storedChildren.slice(0, (page + 1) * limit);
    $[15] = limit;
    $[16] = page;
    $[17] = storedChildren;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const children = t8;
  const canLoadMore = storedChildren.length > 0 && page < maxPages && storedChildren.length > (page + 1) * limit;
  let t9;
  if ($[19] !== params) {
    t9 = params.toString();
    $[19] = params;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const sacParamsKey = t9;
  let t10;
  if ($[21] !== key || $[22] !== page) {
    t10 = () => {
      if (page < maxPages) {
        setPage(page + 1);
        storage.set(key("sacPage"), String(page + 1));
      }
    };
    $[21] = key;
    $[22] = page;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const incrementPage = t10;
  let t11;
  if ($[24] !== ageMax || $[25] !== ageMin || $[26] !== birthDay || $[27] !== birthMonth || $[28] !== countries || $[29] !== disability || $[30] !== filteredCountries || $[31] !== gender || $[32] !== hasActiveCriteria || $[33] !== isInHivAffectedArea || $[34] !== isOrphan || $[35] !== isVulnerable || $[36] !== key || $[37] !== limit || $[38] !== sacParamsKey) {
    t11 = () => {
      let cancelled = false;
      const referrer = String(cookieStore?.referrer);
      const hasMatchingSession = () => {
        const timestamp = Number(storage.get(key("sacTimestamp")));
        const isExpired = Number.isNaN(timestamp) || Date.now() - timestamp > cacheTtlMs;
        return !isExpired && storage.compare([key("sacRef"), key("sacParams")], [referrer, sacParamsKey]);
      };
      const loadChildren = async () => {
        if (hasMatchingSession()) {
          const cached = storage.readChildStore(key("sacChildren"));
          if (cached) {
            const raw = storage.get(key("sacPage"));
            const restoredPage = Number.isNaN(Number(raw)) ? 0 : Number(raw);
            selectedChildRef.current = sessionStorage.getItem("sacSelectedChild") ?? undefined;
            if (!cancelled) {
              setStoredChildren(cached.children as ChildAvailabilityItem[] || []);
              setPage(restoredPage);
              setIsLoading(false);
            }
            return;
          }
        }
        sessionStorage.removeItem("sacSelectedChild");
        setStoredChildren([]);
        setIsLoading(true);
        setPage(0);
        const results = hasActiveCriteria ? await childSearch({
          take: limit * 10,
          skip: 0,
          gender: gender as ChildGenderEnum,
          ageMin,
          ageMax,
          countries: isVulnerable ? filteredCountries : countries,
          birthDay,
          birthMonth,
          isOrphan,
          isInHivAffectedArea,
          disability,
          referrer
        }) : await getRandomChildren(limit * 10);
        if (cancelled) {
          return;
        }
        storage.set([key("sacChildren"), key("sacRef"), key("sacParams"), key("sacPage"), key("sacTimestamp")], [JSON.stringify(results), referrer, sacParamsKey, "0", String(Date.now())]);
        setStoredChildren(results?.children as ChildAvailabilityItem[] || []);
        setPage(0);
        setIsLoading(false);
      };
      loadChildren();
      return () => {
        cancelled = true;
      };
    };
    $[24] = ageMax;
    $[25] = ageMin;
    $[26] = birthDay;
    $[27] = birthMonth;
    $[28] = countries;
    $[29] = disability;
    $[30] = filteredCountries;
    $[31] = gender;
    $[32] = hasActiveCriteria;
    $[33] = isInHivAffectedArea;
    $[34] = isOrphan;
    $[35] = isVulnerable;
    $[36] = key;
    $[37] = limit;
    $[38] = sacParamsKey;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== ageMax || $[41] !== ageMin || $[42] !== birthDay || $[43] !== birthMonth || $[44] !== countries || $[45] !== disability || $[46] !== filteredCountries || $[47] !== gender || $[48] !== hasActiveCriteria || $[49] !== isInHivAffectedArea || $[50] !== isOrphan || $[51] !== isVulnerable || $[52] !== limit || $[53] !== sacParamsKey) {
    t12 = [gender, ageMax, ageMin, countries, filteredCountries, disability, isInHivAffectedArea, isOrphan, isVulnerable, birthDay, birthMonth, limit, sacParamsKey, hasActiveCriteria];
    $[40] = ageMax;
    $[41] = ageMin;
    $[42] = birthDay;
    $[43] = birthMonth;
    $[44] = countries;
    $[45] = disability;
    $[46] = filteredCountries;
    $[47] = gender;
    $[48] = hasActiveCriteria;
    $[49] = isInHivAffectedArea;
    $[50] = isOrphan;
    $[51] = isVulnerable;
    $[52] = limit;
    $[53] = sacParamsKey;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  useEffect(t11, t12);
  let t13;
  if ($[55] !== children?.length || $[56] !== isLoading) {
    t13 = () => {
      if (!isLoading && selectedChildRef.current && children?.length) {
        scrollToElement(selectedChildRef.current);
        sessionStorage.removeItem("sacSelectedChild");
        selectedChildRef.current = undefined;
      }
    };
    $[55] = children?.length;
    $[56] = isLoading;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== children || $[59] !== isLoading) {
    t14 = [isLoading, children];
    $[58] = children;
    $[59] = isLoading;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  useEffect(t13, t14);
  let t15;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [];
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  useEffect(_temp, t15);
  const t16 = !hasActiveCriteria;
  const t17 = isDynamic && canLoadMore;
  let t18;
  if ($[62] !== children || $[63] !== incrementPage || $[64] !== isLoading || $[65] !== limit || $[66] !== resetFilters || $[67] !== t16 || $[68] !== t17) {
    t18 = {
      isLoading,
      children,
      showRichMix: t16,
      showLoadMoreButton: t17,
      incrementPage,
      resetFilters,
      limit
    };
    $[62] = children;
    $[63] = incrementPage;
    $[64] = isLoading;
    $[65] = limit;
    $[66] = resetFilters;
    $[67] = t16;
    $[68] = t17;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  return t18;
};
function _temp() {
  if (globalThis.history?.scrollRestoration) {
    globalThis.history.scrollRestoration = "manual";
  }
}